import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save, Send, MessageCircle, Shield, RotateCcw, RefreshCw, ExternalLink, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror the backend FLOWS + step labels used by WarrantyEmailSettingsModal.
const FLOWS = {
  garantie: {
    title: "Garanții",
    icon: Shield,
    color: "emerald",
    steps: [
      { status: "pending",         label: "Creată (Așteaptă AWB)",     desc: "Stare inițială — fără AWB încă." },
      { status: "pickup_issued",   label: "Preluare Client",            desc: "AWB de ridicare emis." },
      { status: "in_transit",      label: "În drum spre service",       desc: "Curierul a scanat coletul." },
      { status: "at_service",      label: "Verificare Service",         desc: "Coletul a ajuns la noi." },
      { status: "delivery_issued", label: "În livrare către client",    desc: "AWB de livrare emis." },
      { status: "completed",       label: "Finalizată",                 desc: "Coletul livrat clientului." },
    ],
  },
  retur: {
    title: "Retururi",
    icon: RotateCcw,
    color: "sky",
    steps: [
      { status: "pending",                     label: "Cerere Creată",                  desc: "Stare inițială — fără AWB încă." },
      { status: "pickup_issued",               label: "Preluare Client",                desc: "AWB de ridicare emis." },
      { status: "in_transit_warehouse",        label: "Către Depozit",                  desc: "Curierul a scanat coletul." },
      { status: "at_warehouse_pending_refund", label: "În verificare, Bani de returnat", desc: "Coletul a ajuns la depozit." },
      { status: "completed",                   label: "Finalizată (Bani Returnați)",    desc: "MANUAL – admin apasă Bani Returnați." },
    ],
  },
  schimb: {
    title: "Schimb",
    icon: RefreshCw,
    color: "violet",
    steps: [
      { status: "pending",              label: "În așteptare",                desc: "Stare inițială — cerere creată, fără AWB emis încă." },
      { status: "outbound_in_delivery", label: "În livrare către client",    desc: "AWB-urile Sameday (Livrare + Ridicare) au fost emise." },
      { status: "return_in_transit",    label: "În tranzit către depozit",   desc: "AWB Livrare = LIVRAT → curierul ridică coletul vechi." },
      { status: "completed",            label: "Finalizată",                 desc: "AWB Ridicare = LIVRAT la depozit." },
    ],
  },
  livrare: {
    title: "Livrare",
    icon: Truck,
    color: "sky",
    steps: [
      { status: "pending",     label: "Cerere Livrare Creată", desc: "Stare inițială — cerere de livrare creată, fără AWB emis încă." },
      { status: "in_delivery", label: "În drum spre client",   desc: "AWB Sameday emis (opțional cu ramburs)." },
      { status: "completed",   label: "Finalizată",            desc: "AWB = LIVRAT la client." },
    ],
  },
};

const key = (flow, status) => `tpl_wa_${flow}_${status}`;

function TabButton({ flow, def, active, onClick }) {
  const Icon = def.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`wa-warranty-tab-${flow}`}
      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 ${
        active ? "border-emerald-400 text-emerald-200 bg-emerald-500/5" : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/40"
      }`}
    >
      <Icon className="h-4 w-4" /> {def.title}
    </button>
  );
}

function StepRow({ flow, step, cfg, templates, update, sendTest, sendingKey, overridePhone, setOverridePhone }) {
  const k = key(flow, step.status);
  const isTesting = sendingKey === `${flow}.${step.status}`;
  const hasTpl = !!cfg[k];
  const approved = templates.filter((t) => t.meta_status === "APPROVED");

  return (
    <div data-testid={`wa-warranty-step-${flow}-${step.status}`}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-100">{step.label}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{step.desc}</div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-mono shrink-0">{step.status}</div>
      </div>
      <select data-testid={`wa-warranty-${k}-select`}
        value={cfg[k] || ""}
        onChange={(e) => update(k, e.target.value || null)}
        className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none">
        <option value="">— Nu trimite WhatsApp pentru acest pas —</option>
        {approved.map((t) => (
          <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
        ))}
        {approved.length === 0 && (
          <option value="" disabled>Niciun șablon APPROVED de Meta. Creează în Șabloane → WhatsApp și submit-ează.</option>
        )}
      </select>
      <div className="flex items-center gap-2">
        <input
          data-testid={`wa-warranty-${k}-test-phone`}
          type="tel"
          value={overridePhone}
          onChange={(e) => setOverridePhone(e.target.value)}
          placeholder="Număr test (ex: 40751234567)"
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200 outline-none"
        />
        <button
          data-testid={`wa-warranty-${k}-test-send`}
          disabled={!hasTpl || isTesting}
          onClick={() => sendTest(flow, step.status)}
          title={!hasTpl ? "Selectează un șablon mai întâi" : "Trimite WhatsApp test cu date demo"}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-100 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="h-3.5 w-3.5" /> {isTesting ? "Se trimite..." : "Test"}
        </button>
      </div>
    </div>
  );
}

export default function WhatsAppWarrantySettingsModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingKey, setSendingKey] = useState(null);
  const [activeTab, setActiveTab] = useState("garantie");
  const [overridePhone, setOverridePhone] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/returns/warranty/whatsapp-config`),
          axios.get(`${API}/whatsapp-templates`),
        ]);
        setCfg(a.data);
        setTemplates(b.data.items || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/returns/warranty/whatsapp-config`, cfg);
      setCfg(r.data);
      toast.success("Setări WhatsApp salvate.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (flow, status) => {
    const k = `${flow}.${status}`;
    setSendingKey(k);
    try {
      const r = await axios.post(`${API}/returns/warranty/test-step-whatsapp`, {
        flow, status, to: overridePhone || undefined,
      });
      if (r.data?.sent) {
        toast.success(`WhatsApp test trimis către ${r.data.to} (${r.data.template_name || ""})`);
      } else {
        toast.error("Nu s-a trimis nimic.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.error?.message || e.message;
      toast.error(msg || "Eroare la trimitere test");
    } finally {
      setSendingKey(null);
    }
  };

  const flowDef = FLOWS[activeTab];

  if (!cfg) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="rounded-xl bg-zinc-900 p-6 text-zinc-300">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div data-testid="wa-warranty-modal" className="w-full max-w-3xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between border-b border-zinc-800 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-zinc-50 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-400" /> Setări WhatsApp — Returnări & Garanții
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Configurează șabloanele WhatsApp trimise automat la fiecare pas al cererii, per Garanție / Retur / Schimb / Livrare.
            </p>
          </div>
          <button onClick={onClose} data-testid="wa-warranty-close"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-zinc-800 px-6 py-3 bg-zinc-900/40">
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="wa-warranty-enabled" type="checkbox"
              checked={cfg.enabled !== false}
              onChange={(e) => update("enabled", e.target.checked)}
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-800 text-emerald-500" />
            <span className="text-sm font-medium text-zinc-200">Trimite automat WhatsApp (master switch)</span>
            <span className="text-[11px] text-zinc-500">— dacă e dezactivat, niciun pas nu trimite WhatsApp.</span>
          </label>
        </div>

        <div className="flex border-b border-zinc-800 bg-zinc-950">
          {Object.entries(FLOWS).map(([f, def]) => (
            <TabButton key={f} flow={f} def={def} active={activeTab === f} onClick={() => setActiveTab(f)} />
          ))}
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-3 flex-1">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200 leading-relaxed">
            Doar șabloanele <span className="font-semibold">APPROVED</span> de Meta apar în dropdown. Șabloanele PENDING sau REJECTED nu pot fi trimise.{" "}
            <a href="/email-templates" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-amber-100 underline">
              Deschide Șabloane → tab WhatsApp <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {flowDef && flowDef.steps.map((step) => (
            <StepRow key={step.status} flow={activeTab} step={step}
              cfg={cfg} templates={templates} update={update}
              sendTest={sendTest} sendingKey={sendingKey}
              overridePhone={overridePhone} setOverridePhone={setOverridePhone} />
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-6 py-3">
          <button onClick={onClose}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-200 hover:border-zinc-600">
            Anulează
          </button>
          <button data-testid="wa-warranty-save" disabled={saving} onClick={save}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/20 px-3 py-1.5 text-sm font-medium text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
}
